// A number that ticks to its new value (ADR-0043): the floor's figures and Sell's total count up or down for about
// half a second when they change, so a change is noticed and its direction read. Motivated motion — feedback that a
// figure moved — and only then: the first render shows the value, nothing ticks on its own.
//
// The frames write the text node React already owns (`nodeValue`), never through React state, so a tick costs no
// renders; React's own value is always the final one, so a re-render mid-tick lands on the right number. It ticks only
// where the reader has stated no preference against motion — a browser asked for reduced motion, or a test's jsdom
// with no `matchMedia`, shows the new value at once.

import type { Money } from '@clavian/api-client';
import { useLayoutEffect, useRef, type ReactNode } from 'react';

const DURATION_MS = 560;

function motionAllowed(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    typeof window.requestAnimationFrame === 'function' &&
    window.matchMedia('(prefers-reduced-motion: no-preference)').matches
  );
}

export interface TickingNumberProps {
  /** The value to show, an integer (a count, or money in minor units). */
  readonly value: number;
  /** How a value reads: a count as itself, money through the venue's formatter. */
  readonly format: (value: number) => string;
  readonly className?: string;
}

export function TickingNumber({ value, format, className }: TickingNumberProps): ReactNode {
  const node = useRef<HTMLSpanElement | null>(null);
  const shown = useRef(value);
  const formatRef = useRef(format);
  // The latest formatter for the frames, kept after each render (before the tick below runs).
  useLayoutEffect(() => {
    formatRef.current = format;
  });

  useLayoutEffect(() => {
    const from = shown.current;
    const text = node.current?.firstChild;
    if (from === value || text === null || text === undefined || text.nodeType !== Node.TEXT_NODE || !motionAllowed()) {
      shown.current = value;
      return undefined;
    }
    const started = performance.now();
    let frame = 0;
    const paint = (current: number) => {
      shown.current = current;
      text.nodeValue = formatRef.current(current);
    };
    const step = (now: number) => {
      const progress = Math.min(1, (now - started) / DURATION_MS);
      const eased = 1 - (1 - progress) ** 3;
      paint(Math.round(from + (value - from) * eased));
      if (progress < 1) frame = window.requestAnimationFrame(step);
    };
    // Before the browser paints React's final text, put the old value back, so the tick starts where the eye was.
    paint(from);
    frame = window.requestAnimationFrame(step);
    return () => {
      window.cancelAnimationFrame(frame);
      paint(value);
    };
  }, [value]);

  return (
    <span ref={node} className={className}>
      {format(value)}
    </span>
  );
}

/**
 * An amount of money that ticks to its new value: the integer minor units tick, each frame is written through the
 * venue's formatter in the same currency, and the last frame — like React's own text — is the exact amount. Never a
 * float: the in-between amounts are whole minor units too.
 */
export function TickingMoney({ value, format, className }: { readonly value: Money; readonly format: (value: Money) => string; readonly className?: string }): ReactNode {
  return <TickingNumber className={className} value={value.amount} format={(amount) => format({ amount, currency: value.currency })} />;
}
